import { useEffect, useState } from "react";
import { Flame, Plus, Save, ShieldAlert, ShieldCheck, X } from "lucide-react";
import { ContainerCard } from "../../components/ui/cards";
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../../components/common/ToastMsg";
import { LEAD_PRIORITY_CONFIG_URL } from "../../constants/api";
import { cn } from "../../utils/tailwindUtils";

/**
 * Settings -> Lead Priority (trustedge customers/lead_quality.py).
 *
 * Which lead sources the lead list marks "Priority" and lets reps filter to,
 * picked from every source in use with its lead count and win rate. Plus the
 * Genuine / Check lead badge, worked out from the phone, the name and call
 * outcomes - nothing is stored per lead, so a change here re-badges every lead.
 */

const norm = (s) => (s || "").trim().toLowerCase();

function Toggle({ on, onClick, disabled }) {
    return (
        <button type="button" disabled={disabled} onClick={onClick} aria-pressed={on}
            className={cn("relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors",
                on ? "bg-primary" : "bg-gray-300 dark:bg-gray-600", disabled && "opacity-50 cursor-not-allowed")}>
            <span className={cn("inline-block h-4 w-4 transform rounded-full bg-white transition-transform",
                on ? "translate-x-6" : "translate-x-1")} />
        </button>
    );
}

export default function LeadPriority() {
    const { fetch, update } = useApiMethods();
    const [cfg, setCfg]       = useState(null);
    const [draft, setDraft]   = useState(null);
    const [sources, setSources] = useState([]);
    const [custom, setCustom] = useState("");
    const [saving, setSaving] = useState(false);

    const apply = (res) => { setCfg(res.data); setDraft(res.data); setSources(res.sources || []); };

    useEffect(() => {
        fetch(LEAD_PRIORITY_CONFIG_URL).then(res => {
            if (res?.status === "success") apply(res);
            else ToastMsg("error", res?.message || "Could not load lead priority");
        });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    if (!draft) return <ContainerCard><div className="p-6 text-xs text-gray-400">Loading…</div></ContainerCard>;

    const picked = new Set(draft.priority_sources.map(norm));
    const dirty = cfg.is_enabled !== draft.is_enabled || cfg.check_quality !== draft.check_quality
        || cfg.priority_sources.join("\n") !== draft.priority_sources.join("\n");

    const toggleSource = (name) => setDraft(d => ({
        ...d,
        priority_sources: picked.has(norm(name))
            ? d.priority_sources.filter(s => norm(s) !== norm(name))
            : [...d.priority_sources, name.trim()],
    }));
    const addCustom = () => {
        const v = custom.trim();
        if (v && !picked.has(norm(v))) toggleSource(v);
        setCustom("");
    };

    // Picked sources that no lead carries yet still show, so they can be removed.
    const unused = draft.priority_sources.filter(s => !sources.some(x => norm(x.source) === norm(s)));

    const save = async () => {
        setSaving(true);
        try {
            const res = await update(LEAD_PRIORITY_CONFIG_URL, {
                is_enabled: draft.is_enabled, check_quality: draft.check_quality,
                priority_sources: draft.priority_sources,
            });
            if (res?.status === "success") { ToastMsg("success", res.message); apply(res); }
            else ToastMsg("error", res?.message || "Could not save");
        } finally { setSaving(false); }
    };

    return (
        <ContainerCard>
            <div className="p-4 space-y-6 overflow-auto">
                <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-2">
                        <Flame className="h-5 w-5 text-primary" />
                        <div>
                            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Lead Priority</h2>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                Highlight leads from the sources that turn into customers, and flag leads that look fake.
                            </p>
                        </div>
                    </div>
                    <button onClick={save} disabled={!dirty || saving}
                        className="h-8 px-3 rounded-md text-xs font-medium bg-primary hover:opacity-90 disabled:opacity-40 text-white flex items-center gap-1.5">
                        <Save className="h-3.5 w-3.5" /> {saving ? "Saving…" : "Save"}
                    </button>
                </div>

                {/* ── priority sources ── */}
                <section className="rounded-xl border border-gray-200 dark:border-gray-700 p-4 space-y-3">
                    <div className="flex items-start justify-between gap-4">
                        <div className="text-xs">
                            <p className="font-semibold text-gray-900 dark:text-white text-sm">Priority sources {draft.is_enabled ? "- ON" : "- OFF"}</p>
                            <p className="text-gray-600 dark:text-gray-400 mt-1 max-w-2xl">
                                Leads from these sources get a <b>Priority</b> badge and an orange edge in the lead list,
                                and the <b>Priority</b> chip there lists the open ones. Tick the sources below.
                            </p>
                        </div>
                        <Toggle on={draft.is_enabled} onClick={() => setDraft({ ...draft, is_enabled: !draft.is_enabled })} />
                    </div>

                    <div className={cn("overflow-x-auto", !draft.is_enabled && "opacity-60")}>
                        <table className="w-full text-xs">
                            <thead>
                                <tr className="text-left text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                                    <th className="py-1.5 pr-2 w-8"></th>
                                    <th className="py-1.5 pr-2 font-medium">Source</th>
                                    <th className="py-1.5 pr-2 font-medium text-right">Leads</th>
                                    <th className="py-1.5 pr-2 font-medium text-right">Won</th>
                                    <th className="py-1.5 font-medium text-right">Win rate</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sources.map(s => (
                                    <tr key={s.source} className="border-b border-gray-100 dark:border-gray-700/60 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/40"
                                        onClick={() => toggleSource(s.source)}>
                                        <td className="py-1.5 pr-2"><input type="checkbox" readOnly checked={picked.has(norm(s.source))} /></td>
                                        <td className="py-1.5 pr-2 text-gray-800 dark:text-gray-100">{s.source}</td>
                                        <td className="py-1.5 pr-2 text-right tabular-nums">{s.leads}</td>
                                        <td className="py-1.5 pr-2 text-right tabular-nums">{s.won}</td>
                                        <td className="py-1.5 text-right tabular-nums">
                                            <span className={cn(s.win_rate >= 5 && "font-semibold text-emerald-700 dark:text-emerald-400")}>{s.win_rate}%</span>
                                        </td>
                                    </tr>
                                ))}
                                {unused.map(s => (
                                    <tr key={s} className="border-b border-gray-100 dark:border-gray-700/60 cursor-pointer" onClick={() => toggleSource(s)}>
                                        <td className="py-1.5 pr-2"><input type="checkbox" readOnly checked /></td>
                                        <td className="py-1.5 pr-2 text-gray-800 dark:text-gray-100">{s}</td>
                                        <td colSpan={3} className="py-1.5 text-right text-gray-400">no leads yet</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="flex items-center gap-2">
                        <input value={custom} onChange={e => setCustom(e.target.value)} maxLength={50}
                            onKeyDown={e => { if (e.key === "Enter") addCustom(); }}
                            placeholder="Another source name, exactly as leads carry it"
                            className="flex-1 max-w-sm text-xs rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1.5" />
                        <button type="button" onClick={addCustom} disabled={!custom.trim()}
                            className="h-7 px-2 rounded-md text-xs border border-gray-300 dark:border-gray-600 flex items-center gap-1 disabled:opacity-40">
                            <Plus className="h-3 w-3" /> Add
                        </button>
                        {draft.priority_sources.length > 0 && (
                            <button type="button" onClick={() => setDraft({ ...draft, priority_sources: [] })}
                                className="h-7 px-2 rounded-md text-xs text-gray-500 flex items-center gap-1 hover:text-gray-800">
                                <X className="h-3 w-3" /> Clear
                            </button>
                        )}
                    </div>
                    {cfg.is_default && (
                        <p className="text-[11px] text-gray-500 dark:text-gray-400">
                            Not saved yet - using the defaults: {cfg.default_sources.join(", ")}.
                        </p>
                    )}
                </section>

                {/* ── genuine / check ── */}
                <section className="rounded-xl border border-gray-200 dark:border-gray-700 p-4 space-y-3">
                    <div className="flex items-start justify-between gap-4">
                        <div className="text-xs">
                            <p className="font-semibold text-gray-900 dark:text-white text-sm">Genuine / fake check {draft.check_quality ? "- ON" : "- OFF"}</p>
                            <p className="text-gray-600 dark:text-gray-400 mt-1 max-w-2xl">
                                Hover a badge in the lead list to see why it was given.
                            </p>
                        </div>
                        <Toggle on={draft.check_quality} onClick={() => setDraft({ ...draft, check_quality: !draft.check_quality })} />
                    </div>
                    <div className={cn("grid gap-3 md:grid-cols-2 text-xs", !draft.check_quality && "opacity-60")}>
                        <div className="rounded-lg border border-emerald-200 dark:border-emerald-800/50 bg-emerald-50/50 dark:bg-emerald-900/10 p-3">
                            <p className="flex items-center gap-1 font-semibold text-emerald-700 dark:text-emerald-300"><ShieldCheck className="h-3.5 w-3.5" /> Genuine</p>
                            <ul className="mt-1.5 list-disc pl-4 space-y-0.5 text-gray-600 dark:text-gray-300">
                                <li>A call was logged as connected</li>
                                <li>Reached Qualified, a demo, proposal or later</li>
                                <li>Came through an eMedHub referral or a named channel partner</li>
                                <li>Same number as an existing customer</li>
                            </ul>
                        </div>
                        <div className="rounded-lg border border-red-200 dark:border-red-800/50 bg-red-50/50 dark:bg-red-900/10 p-3">
                            <p className="flex items-center gap-1 font-semibold text-red-700 dark:text-red-300"><ShieldAlert className="h-3.5 w-3.5" /> Check lead</p>
                            <ul className="mt-1.5 list-disc pl-4 space-y-0.5 text-gray-600 dark:text-gray-300">
                                <li>A call was logged as wrong number</li>
                                <li>No phone, too short, 1234567890, or one digit repeated</li>
                                <li>10 digits that cannot be an Indian mobile (landlines are not flagged)</li>
                                <li>Name like "test", "abc", "hi", only digits or one letter</li>
                                <li>A phone number typed into the name, or a random bot-made name</li>
                            </ul>
                            <p className="mt-1.5 text-[11px] text-gray-500 dark:text-gray-400">A connected call always wins over these.</p>
                        </div>
                    </div>
                </section>
            </div>
        </ContainerCard>
    );
}
